// THE HAAL PROJECT — NAMES REFERENCE DATA
// All Names appearing across the IRL Lens sequences
// Organized by frequency, linked sequences, Quranic references, cross-Key connections

export interface NameQuranicRef {
  ref: string;
  text: string;
}

export interface NameSequenceAppearance {
  seqId: string;
  seqNum: number;
  seqTitle: string;
  city: string;
  biome: string;
  role: string; // why this Name is active in this sequence
  layer: 'encounter' | 'names' | 'sunnat' | 'zahir-batin' | 'haqq' | 'amanah';
}

export interface NameKeyConnection {
  keyId: string; // AYAT | HAAL | TAQWAH | MIZAN | AMANAH
  keyNum: string;
  connection: string;
}

export interface NameSunnatStep {
  step: number;
  seqId: string;
  seqTitle: string;
  description: string;
}

export interface NameEntry {
  id: string;
  ar: string;
  transliteration: string;
  meaning: string;
  root: string;
  rootMeaning: string;
  category: 'Creation' | 'Provision' | 'Knowledge' | 'Power' | 'Justice' | 'Mercy' | 'Guidance' | 'Transformation' | 'Community' | 'Hidden';
  frequency: number; // number of sequences this Name appears in
  quranicRefs: NameQuranicRef[];
  sequences: NameSequenceAppearance[];
  keyConnections: NameKeyConnection[];
  sunnatSteps: NameSunnatStep[]; // specific Sunnat steps where this Name is named
  crossNameConnections: string[]; // other Name IDs that frequently appear with this Name
  fieldPrompt: string; // observation prompt for caretakers
  letterSource?: string; // which letter/passage this Name appears in (e.g., '59:23-24', 'hadith-99')
  isAnchor?: boolean; // featured anchor Name
  pairedWith?: string; // ID of paired Name (e.g., zahir ↔ batin)
}

// ─── SEQUENCE REGISTRY ────────────────────────────────────────────────────────

export const SEQUENCE_REGISTRY: Record<string, { title: string; city: string; biome: string; num: number }> = {
  's1': { title: 'Big Bluestem', city: 'Chicago', biome: 'Tallgrass Prairie', num: 1 },
  's2': { title: 'Wetland Filtration', city: 'Detroit', biome: 'Wetland/Marsh', num: 2 },
  's3': { title: 'Canopy Photosynthesis', city: 'Atlanta', biome: 'Forest', num: 3 },
  's4': { title: 'Mycorrhizal Network', city: 'Atlanta', biome: 'Forest · Root Layer', num: 4 },
  's5': { title: 'Amphibian Metamorphosis', city: 'Detroit', biome: 'Wetland · Transition Zone', num: 5 },
  's6': { title: 'Salmon Run', city: 'Portland', biome: 'Rainforest · Migratory Layer', num: 6 },
  's7': { title: 'Longleaf Pine Grass Stage', city: 'Durham', biome: 'Piedmont Transition · Fire Savanna', num: 7 },
  's8': { title: 'River Bluff Geology', city: 'Cincinnati', biome: 'River Bluffs', num: 8 },
  's9': { title: 'Three Rivers Confluence', city: 'Pittsburgh', biome: 'River Confluence', num: 9 },
  's10': { title: 'Monarch Migration', city: 'Chicago', biome: 'Tallgrass Prairie', num: 10 },
  's11': { title: 'Sycamore Sentinel', city: 'Louisville', biome: 'River Bottomland', num: 11 },
  's12': { title: 'Missouri River & Loess Hills', city: 'Omaha', biome: 'Great Plains', num: 12 },
  's13': { title: 'The Waiting Seed', city: 'All Cities', biome: 'Cross-Biome', num: 13 },
  's14': { title: "The Torrent's Foam", city: 'All Cities', biome: 'Cross-Biome', num: 14 },
  's15': { title: 'Thunder & Lightning', city: 'All Cities', biome: 'Cross-Biome', num: 15 },
  's16': { title: 'Spider Silk', city: 'All Forests', biome: 'Cross-Biome', num: 16 },
  's17': { title: 'Duckweed', city: 'Detroit', biome: 'Wetland', num: 17 },
  's18': { title: 'Clay', city: 'All Cities', biome: 'Cross-Biome', num: 18 },
  's19': { title: 'Savannah Acacia', city: 'Atlanta', biome: 'Savannah', num: 19 },
  's20': { title: 'Tidal Estuary', city: 'Portland', biome: 'Coastal', num: 20 },
  's21': { title: 'Date Palm', city: 'All Cities', biome: 'Cross-Biome', num: 21 },
  's22': { title: 'Tidal Rhythm', city: 'Portland', biome: 'Coastal', num: 22 },
  's23': { title: 'Coral Reef', city: 'All Cities', biome: 'Ocean', num: 23 },
  's24': { title: 'Desert Succulent', city: 'All Cities', biome: 'Desert', num: 24 },
  // ... possibly more sequences ...
};

// ─── NAMES DATA ───────────────────────────────────────────────────────────────

export const NAMES_DATA: NameEntry[] = [
  {
    id: 'khaliq',
    ar: 'الْخَالِق',
    transliteration: 'Al-Khaliq',
    meaning: 'The Creator',
    root: 'kh-l-q',
    rootMeaning: 'to create, to bring into existence',
    category: 'Creation',
    frequency: 8,
    quranicRefs: [
      { ref: '59:24', text: 'He is Allah, the Creator, the Evolver, the Fashioner.' },
      { ref: '6:102', text: 'That is Allah, your Lord; there is no deity except Him, the Creator of all things.' },
      { ref: '13:16', text: 'Say: Allah is the Creator of all things.' },
    ],
    sequences: [
      { seqId: 's1', seqNum: 1, seqTitle: 'Big Bluestem', city: 'Chicago', biome: 'Prairie', role: 'Created a grass whose underground reality exceeds its visible reality by 4x', layer: 'names' },
      { seqId: 's3', seqNum: 3, seqTitle: 'Canopy Photosynthesis', city: 'Atlanta', biome: 'Forest', role: 'Created the chlorophyll that captures the photon — the mechanism of divine provision', layer: 'names' },
      { seqId: 's4', seqNum: 4, seqTitle: 'Mycorrhizal Network', city: 'Atlanta', biome: 'Forest', role: 'Created a partnership older than flowers, older than fruit, older than any animal', layer: 'sunnat' },
      { seqId: 's5', seqNum: 5, seqTitle: 'Amphibian Metamorphosis', city: 'Detroit', biome: 'Wetland', role: 'Creates lungs from cells that were gill tissue — new creation from existing material', layer: 'names' },
      { seqId: 's6', seqNum: 6, seqTitle: 'Salmon Run', city: 'Portland', biome: 'Rainforest', role: 'Creation begins in the specific medium designed for it — egg in gravel', layer: 'sunnat' },
      { seqId: 's11', seqNum: 11, seqTitle: 'Sycamore Sentinel', city: 'Louisville', biome: 'Bottomland', role: 'Created a tree whose growth requires visible shedding of its previous form', layer: 'names' },
      { seqId: 's13', seqNum: 13, seqTitle: 'The Waiting Seed', city: 'All Cities', biome: 'Cross-Biome', 
        role: 'Kun fayakun made physical — the decree precedes manifestation', layer: 'haqq' 
      },
      { seqId: 's18', seqNum: 18, seqTitle: 'Clay', city: 'All Cities', biome: 'Cross-Biome', role: 'The material of Adam\'s creation is the material of our sustenance', layer: 'haqq' },
    ],
    keyConnections: [
      { keyId: 'AYAT', keyNum: '01', connection: 'Al-Khaliq is the first Name in the Leaf Sequence — the entry point of every Ayat-recognition. "Al-Khaliq created the leaf" is the first move of the IRL Lens.' },
      { keyId: 'AMANAH', keyNum: '05', connection: 'The Amanah of Al-Khaliq: to recognize that every created thing is a trust, not a resource. The Khalifah is the sub-creator — acting in the image of Al-Khaliq through Islah.' },
    ],
    sunnatSteps: [
      { step: 1, seqId: 's6', seqTitle: 'Salmon Run', description: 'Egg deposited in gravel — Al-Khaliq creates in the context appropriate to the first stage' },
      { step: 1, seqId: 's5', seqTitle: 'Amphibian Metamorphosis', description: 'Egg deposited in water — the thing begins in its medium' },
    ],
    crossNameConnections: ['bari', 'musawwir', 'razzaq', 'muhyi'],
    fieldPrompt: 'Where do you see creation happening right now? A seed germinating. A cloud forming. A child learning to walk. Al-Khaliq creates continuously — document where you witness this.',
  },
  {
    id: 'bari',
    ar: 'الْبَارِئ',
    transliteration: 'Al-Bari',
    meaning: 'The Evolver / The Originator of Forms',
    root: 'b-r-a',
    rootMeaning: 'to originate, to evolve, to free from defect',
    category: 'Creation',
    frequency: 5,
    quranicRefs: [
      { ref: '59:24', text: 'He is Allah, the Creator, the Evolver, the Fashioner.' },
      { ref: '2:54', text: 'Turn in repentance to your Creator (Bari) and kill yourselves.' },
    ],
    sequences: [
      { seqId: 's1', seqNum: 1, seqTitle: 'Big Bluestem', city: 'Chicago', biome: 'Prairie', role: 'Grew the grass — the evolutionary unfolding of its form over 10,000 years', layer: 'names' },
      { seqId: 's5', seqNum: 5, seqTitle: 'Amphibian Metamorphosis', city: 'Detroit', biome: 'Wetland', role: 'Evolves the creature through stages it does not understand — four body plans from one genetic alphabet', layer: 'names' },
      { seqId: 's7', seqNum: 7, seqTitle: 'Longleaf Pine Grass Stage', city: 'Durham', biome: 'Savanna', role: 'The grass stage IS Al-Bari — the tree evolving through its prescribed stages', layer: 'names' },
      { seqId: 's13', seqNum: 13, seqTitle: 'The Waiting Seed', city: 'All Cities', biome: 'Cross-Biome', 
        role: 'The seed contains the blueprint for the evolved form — Al-Bari\'s decree encoded', layer: 'names' 
      },
      { seqId: 's18', seqNum: 18, seqTitle: 'Clay', city: 'All Cities', biome: 'Cross-Biome', role: 'The material of Adam\'s creation — Al-Bari evolved the human form from clay', layer: 'haqq' },
    ],
    keyConnections: [
      { keyId: 'AYAT', keyNum: '01', connection: 'Al-Bari is the second Name in the Leaf Sequence — "Al-Bari grew the leaf." Evolution as divine attribute, not random process.' },
      { keyId: 'HAAL', keyNum: '02', connection: 'Al-Bari governs the evolution of Haal — the gradual unfolding of inner states through prescribed stages, like the tadpole becoming a frog.' },
    ],
    sunnatSteps: [
      { step: 2, seqId: 's5', seqTitle: 'Amphibian Metamorphosis', description: 'Tadpole hatches with gills and tail — first body plan: aquatic, herbivorous' },
    ],
    crossNameConnections: ['khaliq', 'musawwir', 'muhawwil'],
    fieldPrompt: 'Where do you see evolution — not random mutation but prescribed unfolding? A caterpillar becoming a butterfly. A community evolving through stages. Al-Bari evolves through stages the creature does not understand.',
  },
  {
    id: 'musawwir',
    ar: 'الْمُصَوِّر',
    transliteration: 'Al-Musawwir',
    meaning: 'The Fashioner of Forms',
    root: 's-w-r',
    rootMeaning: 'to form, to fashion, to give shape',
    category: 'Creation',
    frequency: 9,
    quranicRefs: [
      { ref: '59:24', text: 'He is Allah, the Creator, the Evolver, the Fashioner.' },
      { ref: '3:6', text: 'It is He who forms you in the wombs however He wills.' },
    ],
    sequences: [
      { seqId: 's1', seqNum: 1, seqTitle: 'Big Bluestem', city: 'Chicago', biome: 'Prairie', role: 'Gave the grass its greenness, weight, shape — each a taqdir from Al-Musawwir' },
      { seqId: 's3', seqNum: 3, seqTitle: 'Canopy Photosynthesis', city: 'Atlanta', biome: 'Forest', role: 'The leaf is not making food; it is executing the amr of Ar-Razzaq by the taqdir of Al-Musawwir' },
      { seqId: 's4', seqNum: 4, seqTitle: 'Mycorrhizal Network', city: 'Atlanta', biome: 'Forest', role: 'Creates arbuscules — structures so precise they maximize surface area for exchange within a single cell' },
    ],
    keyConnections: [
      { keyId: 'AYAT', keyNum: '01', connection: 'Al-Musawwir is the third Name in the Leaf Sequence — "Al-Musawwir formed you in the womb."' },
    ],
    sunnatSteps: [],
    crossNameConnections: ['khaliq', 'bari', 'razzaq'],
    fieldPrompt: 'What forms do you see being fashioned around you? The face of a child. The pattern of a community. Al-Musawwir is still fashioning.',
    letterSource: '59:23-24',
    isAnchor: false,
  },
  // ─── AZ-ZAHIR ─────────────────────────────────────────────────────────────
  {
    id: 'zahir',
    ar: 'الظَّاهِر',
    transliteration: 'Az-Zahir',
    meaning: 'The Manifest / The Outwardly Apparent',
    root: 'z-h-r',
    rootMeaning: 'to be manifest, to appear, to be outwardly visible',
    category: 'Knowledge',
    frequency: 7,
    quranicRefs: [
      { ref: '57:3', text: 'He is the First and the Last, the Manifest (Az-Zahir) and the Hidden (Al-Batin), and He is Knowing of all things.' },
      { ref: '6:120', text: 'And leave what is apparent of sin and what is concealed thereof.' },
    ],
    sequences: [
      { seqId: 's1', seqNum: 1, seqTitle: 'Big Bluestem', city: 'Chicago', biome: 'Prairie', role: 'The visible grass above ground — the zahir of a root system 4x deeper than what is seen', layer: 'zahir-batin' },
      { seqId: 's3', seqNum: 3, seqTitle: 'Canopy Photosynthesis', city: 'Atlanta', biome: 'Forest', role: 'The visible leaf surface — the zahir of the photosynthetic process happening within', layer: 'zahir-batin' },
      { seqId: 's5', seqNum: 5, seqTitle: 'Amphibian Metamorphosis', city: 'Detroit', biome: 'Wetland', role: 'The visible body form — the zahir of the metamorphic process encoded in DNA', layer: 'zahir-batin' },
      { seqId: 's6', seqNum: 6, seqTitle: 'Salmon Run', city: 'Portland', biome: 'Rainforest', role: 'The visible upstream migration — the zahir of the olfactory memory guiding the salmon home', layer: 'zahir-batin' },
      { seqId: 's13', seqNum: 13, seqTitle: 'The Waiting Seed', city: 'All Cities', biome: 'Cross-Biome', role: 'The visible dormant seed — the zahir of the complete tree encoded within', layer: 'zahir-batin' },
      { seqId: 's18', seqNum: 18, seqTitle: 'Clay', city: 'All Cities', biome: 'Cross-Biome', role: 'The visible clay — the zahir of the human form that will emerge from it', layer: 'zahir-batin' },
      { seqId: 's14', seqNum: 14, seqTitle: "The Torrent's Foam", city: 'All Cities', biome: 'Cross-Biome', role: 'The foam on the surface — the zahir that passes away while the water beneath endures', layer: 'zahir-batin' },
    ],
    keyConnections: [
      { keyId: 'AYAT', keyNum: '01', connection: 'Az-Zahir is the primary axis of Ayat recognition — every Ayah has a zahir (visible sign) and a batin (hidden meaning). The IRL Lens begins with the zahir and descends to the batin.' },
      { keyId: 'HAAL', keyNum: '02', connection: 'The zahir of Haal is the observable state — behavior, environment, circumstance. The batin of Haal is the inner reality that produces the outer state.' },
      { keyId: 'MIZAN', keyNum: '04', connection: 'The zahir of Mizan is the visible imbalance — food deserts, vacancy rates, health disparities. The batin is the structural injustice that produced them.' },
    ],
    sunnatSteps: [
      { step: 5, seqId: 's1', seqTitle: 'Big Bluestem', description: 'Zahir layer: the visible grass above ground — the manifest form of the underground reality' },
    ],
    crossNameConnections: ['batin', 'basir', 'khabir', 'shahid'],
    fieldPrompt: 'What is the zahir of what you are observing right now? The visible surface. The apparent form. The manifest sign. Az-Zahir is present in everything visible — what does the visible tell you about the hidden?',
    letterSource: '57:3',
    isAnchor: false,
    pairedWith: 'batin',
  },
  // ─── AL-BATIN ─────────────────────────────────────────────────────────────
  {
    id: 'batin',
    ar: 'الْبَاطِن',
    transliteration: 'Al-Batin',
    meaning: 'The Hidden / The Inwardly Concealed',
    root: 'b-t-n',
    rootMeaning: 'to be hidden, to be inward, to be concealed beneath the surface',
    category: 'Hidden',
    frequency: 7,
    quranicRefs: [
      { ref: '57:3', text: 'He is the First and the Last, the Manifest (Az-Zahir) and the Hidden (Al-Batin), and He is Knowing of all things.' },
      { ref: '6:151', text: 'And do not approach immoralities — what is apparent of them and what is concealed.' },
    ],
    sequences: [
      { seqId: 's1', seqNum: 1, seqTitle: 'Big Bluestem', city: 'Chicago', biome: 'Prairie', role: 'The root system 4x deeper than the visible grass — the batin that sustains the zahir', layer: 'zahir-batin' },
      { seqId: 's3', seqNum: 3, seqTitle: 'Canopy Photosynthesis', city: 'Atlanta', biome: 'Forest', role: 'The photosynthetic process within the leaf — the batin of the visible green surface', layer: 'zahir-batin' },
      { seqId: 's4', seqNum: 4, seqTitle: 'Mycorrhizal Network', city: 'Atlanta', biome: 'Forest', role: 'The underground fungal network — the batin of the visible forest above', layer: 'zahir-batin' },
      { seqId: 's5', seqNum: 5, seqTitle: 'Amphibian Metamorphosis', city: 'Detroit', biome: 'Wetland', role: 'The metamorphic process encoded in DNA — the batin of the visible body transformation', layer: 'zahir-batin' },
      { seqId: 's6', seqNum: 6, seqTitle: 'Salmon Run', city: 'Portland', biome: 'Rainforest', role: 'The olfactory memory guiding the salmon — the batin of the visible upstream migration', layer: 'zahir-batin' },
      { seqId: 's13', seqNum: 13, seqTitle: 'The Waiting Seed', city: 'All Cities', biome: 'Cross-Biome', role: 'The complete tree encoded in the dormant seed — the batin of the visible seed', layer: 'zahir-batin' },
      { seqId: 's14', seqNum: 14, seqTitle: "The Torrent's Foam", city: 'All Cities', biome: 'Cross-Biome', role: 'The enduring water beneath the passing foam — the batin that outlasts the zahir', layer: 'zahir-batin' },
    ],
    keyConnections: [
      { keyId: 'AYAT', keyNum: '01', connection: 'Al-Batin is the hidden dimension of every Ayah — the meaning beneath the sign. The IRL Lens descends from zahir to batin through the 8-layer depth panel. Layer 6 is the Batin layer.' },
      { keyId: 'HAAL', keyNum: '02', connection: 'The batin of Haal is the inner reality — the spiritual state, the intention, the root cause. The IRL Lens makes the batin of community Haal visible through the Zahir-Batin axis.' },
      { keyId: 'MIZAN', keyNum: '04', connection: 'The batin of injustice is the structural cause — redlining, disinvestment, policy decisions. The zahir is the food desert. Al-Batin governs the hidden layer of every visible imbalance.' },
    ],
    sunnatSteps: [
      { step: 6, seqId: 's1', seqTitle: 'Big Bluestem', description: 'Batin layer: the underground root system — the hidden reality that sustains the visible grass' },
    ],
    crossNameConnections: ['zahir', 'latif', 'khabir', 'alim'],
    fieldPrompt: 'What is the batin of what you are observing right now? The hidden cause. The concealed structure. The inner reality beneath the surface. Al-Batin is present in everything hidden — what does the hidden tell you about the visible?',
    letterSource: '57:3',
    isAnchor: false,
    pairedWith: 'zahir',
  },
  // ─── AR-RAZZAQ ────────────────────────────────────────────────────────────
  {
    id: 'razzaq',
    ar: 'الرَّزَّاق',
    transliteration: 'Ar-Razzaq',
    meaning: 'The Ever-Provider',
    root: 'r-z-q',
    rootMeaning: 'to provide, to sustain, to give provision',
    category: 'Provision',
    frequency: 11,
    quranicRefs: [
      { ref: '11:6', text: 'There is no creature on earth whose provision is not guaranteed by Allah.' },
      { ref: '51:58', text: 'Indeed, it is Allah who is the Provider (Ar-Razzaq), the firm possessor of strength.' },
      { ref: '2:172', text: 'Eat of the good things We have provided for you.' },
    ],
    sequences: [
      { seqId: 's1', seqNum: 1, seqTitle: 'Big Bluestem', city: 'Chicago', biome: 'Prairie', role: 'The grass provides food for insects, birds, and mammals — Ar-Razzaq flowing through the prairie food web', layer: 'names' },
      { seqId: 's3', seqNum: 3, seqTitle: 'Canopy Photosynthesis', city: 'Atlanta', biome: 'Forest', role: 'Photosynthesis as Ar-Razzaq\'s mechanism — the leaf converts light to sustenance for the entire ecosystem', layer: 'names' },
      { seqId: 's6', seqNum: 6, seqTitle: 'Salmon Run', city: 'Portland', biome: 'Rainforest', role: 'The salmon body becomes provision for the forest after spawning — Ar-Razzaq completing the cycle', layer: 'names' },
      { seqId: 's13', seqNum: 13, seqTitle: 'The Waiting Seed', city: 'All Cities', biome: 'Cross-Biome', role: 'The seed contains provision for the germinating plant — Ar-Razzaq encoded in the endosperm', layer: 'names' },
      { seqId: 's21', seqNum: 21, seqTitle: 'Date Palm', city: 'All Cities', biome: 'Cross-Biome', role: 'The date palm as the most complete provision tree — Ar-Razzaq\'s signature in the desert', layer: 'names' },
    ],
    keyConnections: [
      { keyId: 'AYAT', keyNum: '01', connection: 'Ar-Razzaq is the fifth Name in the Leaf Sequence — the leaf as provision engine. Photosynthesis is Ar-Razzaq\'s mechanism made visible.' },
      { keyId: 'AMANAH', keyNum: '05', connection: 'The Amanah of Ar-Razzaq: to ensure provision flows to all who need it. Food deserts are a transgression against this Amanah. The MRG maps where Ar-Razzaq\'s provision is blocked.' },
    ],
    sunnatSteps: [
      { step: 5, seqId: 's3', seqTitle: 'Canopy Photosynthesis', description: 'Photosynthesis active — Ar-Razzaq\'s provision flowing through chlorophyll' },
    ],
    crossNameConnections: ['khaliq', 'musawwir', 'nur', 'muhyi'],
    fieldPrompt: 'Where is provision flowing right now? A food pantry. A community garden. A masjid distribution. Ar-Razzaq uses human hands — where are you a vessel of provision?',
    letterSource: '59:23-24',
    isAnchor: false,
  },
  // ─── AN-NUR ───────────────────────────────────────────────────────────────
  {
    id: 'nur',
    ar: 'النُّور',
    transliteration: 'An-Nur',
    meaning: 'The Light',
    root: 'n-w-r',
    rootMeaning: 'to illuminate, to give light, to make visible',
    category: 'Guidance',
    frequency: 6,
    quranicRefs: [
      { ref: '24:35', text: 'Allah is the Light of the heavens and the earth. The example of His light is like a niche within which is a lamp.' },
      { ref: '5:15', text: 'There has come to you from Allah a light and a clear Book.' },
      { ref: '39:22', text: 'Is one whose breast Allah has expanded to Islam and who is upon a light from his Lord [like one who is in darkness]?' },
    ],
    sequences: [
      { seqId: 's3', seqNum: 3, seqTitle: 'Canopy Photosynthesis', city: 'Atlanta', biome: 'Forest', role: 'The leaf receives An-Nur — sunlight as the closing element of the Leaf Sequence, converting light to sustenance', layer: 'names' },
      { seqId: 's15', seqNum: 15, seqTitle: 'Thunder & Lightning', city: 'All Cities', biome: 'Cross-Biome', role: 'Lightning as An-Nur made sudden and overwhelming — the light that precedes the sound', layer: 'names' },
      { seqId: 's20', seqNum: 20, seqTitle: 'Tidal Estuary', city: 'Portland', biome: 'Coastal', role: 'Light penetrating the water column — An-Nur enabling photosynthesis in the estuary', layer: 'names' },
    ],
    keyConnections: [
      { keyId: 'AYAT', keyNum: '01', connection: 'An-Nur is the sixth and closing Name in the Leaf Sequence — the arc completes with light. Every recognition arc ends in illumination: الْحَمْدُ لِلَّه.' },
      { keyId: 'HAAL', keyNum: '02', connection: 'An-Nur governs the illumination of Haal — the moment when the inner state becomes clear. The IRL Lens is a tool for receiving An-Nur\'s illumination through creation.' },
    ],
    sunnatSteps: [
      { step: 6, seqId: 's3', seqTitle: 'Canopy Photosynthesis', description: 'Light received — An-Nur completing the Leaf Sequence arc' },
    ],
    crossNameConnections: ['razzaq', 'hadi', 'basir'],
    fieldPrompt: 'Where is light enabling life right now? The sun on a garden. Knowledge illuminating a community. An-Nur is the closing state of every recognition arc — what has been illuminated for you today?',
    letterSource: '24:35',
    isAnchor: false,
  },
  // ─── AL-QADIR ─────────────────────────────────────────────────────────────
  {
    id: 'qadir',
    ar: 'الْقَادِر',
    transliteration: 'Al-Qadir',
    meaning: 'The All-Powerful',
    root: 'q-d-r',
    rootMeaning: 'to have power, to be capable, to decree',
    category: 'Power',
    frequency: 5,
    quranicRefs: [
      { ref: '6:65', text: 'Say: He is the one able (Al-Qadir) to send upon you affliction from above you or from beneath your feet.' },
      { ref: '36:81', text: 'Is not He who created the heavens and earth able (Al-Qadir) to create the likes of them?' },
      { ref: '75:40', text: 'Is that [Creator] not able (Al-Qadir) to give life to the dead?' },
    ],
    sequences: [
      { seqId: 's1', seqNum: 1, seqTitle: 'Big Bluestem', city: 'Chicago', biome: 'Prairie', role: 'Al-Qadir\'s decree precedes the grass — the power behind every germination', layer: 'names' },
      { seqId: 's13', seqNum: 13, seqTitle: 'The Waiting Seed', city: 'All Cities', biome: 'Cross-Biome', role: 'Kun fayakun — Al-Qadir\'s decree encoded in the waiting seed', layer: 'haqq' },
      { seqId: 's15', seqNum: 15, seqTitle: 'Thunder & Lightning', city: 'All Cities', biome: 'Cross-Biome', role: 'Lightning as Al-Qadir\'s power made sudden and visible', layer: 'names' },
    ],
    keyConnections: [
      { keyId: 'AYAT', keyNum: '01', connection: 'Al-Qadir is the first Name in the Leaf Sequence — recognition begins with acknowledging divine capacity. Before the leaf, there is Al-Qadir.' },
      { keyId: 'AMANAH', keyNum: '05', connection: 'The Amanah of Al-Qadir: to act with the power given to us as Khalifah. The caretaker\'s capacity is a trust from Al-Qadir.' },
    ],
    sunnatSteps: [
      { step: 1, seqId: 's1', seqTitle: 'Big Bluestem', description: 'Al-Qadir\'s decree precedes manifestation — the power behind every created thing' },
    ],
    crossNameConnections: ['khaliq', 'bari', 'musawwir', 'razzaq'],
    fieldPrompt: 'Where do you see power operating right now — not force, but the quiet capacity behind growth? Al-Qadir\'s power is present in every germinating seed, every healing wound, every community rebuilding.',
    letterSource: 'hadith-99',
    isAnchor: false,
  },
];

// ─── SUNNAT TIMELINES ─────────────────────────────────────────────────────────
// The Leaf Sequence and other key sequences as step-by-step timelines

export interface SunnatTimelineStep {
  step: number;
  description: string;
  namesActive: string[];
  amr?: string;
  note?: string;
}

export interface SunnatTimeline {
  seqId: string;
  seqTitle: string;
  city: string;
  biome: string;
  ayah: string;
  steps: SunnatTimelineStep[];
}

export const SUNNAT_TIMELINES_DATA: SunnatTimeline[] = [
  {
    seqId: 'leaf-sequence',
    seqTitle: 'The Leaf Sequence',
    city: 'All Cities',
    biome: 'Cross-Biome · Primary Tutorial',
    ayah: 'He is Allah, the Creator, the Evolver, the Fashioner. To Him belong the best Names. (59:24)',
    steps: [
      { step: 1, description: 'Al-Qadir decrees — power precedes manifestation', namesActive: ['Al-Qadir'], amr: 'Kun', note: 'Before the leaf, there is divine capacity. The decree precedes the form.' },
      { step: 2, description: 'Al-Khaliq creates — blueprint encoded in seed', namesActive: ['Al-Khaliq'], amr: 'Fayakun', note: 'The leaf did not design itself. Al-Khaliq originates the blueprint.' },
      { step: 3, description: 'Al-Bari evolves — prescribed unfolding through stages', namesActive: ['Al-Bari'], note: 'Each leaf shaped precisely through evolutionary unfolding. No two identical.' },
      { step: 4, description: 'Al-Musawwir fashions — visible form given to the created thing', namesActive: ['Al-Musawwir'], note: 'The color, the veins, the texture. Zahir made manifest.' },
      { step: 5, description: 'Ar-Razzaq provides — photosynthesis as provision mechanism', namesActive: ['Ar-Razzaq'], note: 'The leaf is a provision engine. Rizq flows through the chlorophyll.' },
      { step: 6, description: 'An-Nur illuminates — light received, arc complete', namesActive: ['An-Nur'], note: 'The sequence closes with light. الْحَمْدُ لِلَّه — the closing state of every recognition arc.' },
    ],
  },
  {
    seqId: 's4',
    seqTitle: 'Mycorrhizal Network',
    city: 'Atlanta',
    biome: 'Forest · Root Layer',
    ayah: 'And in the earth are neighboring plots and gardens of grapevines. (13:4)',
    steps: [
      { step: 1, description: 'Spore germinates in soil — Al-Khaliq creates the fungal thread', namesActive: ['Al-Khaliq', 'Al-Muhyi'] },
      { step: 2, description: 'Hyphal growth — Al-Bari evolves the network structure', namesActive: ['Al-Bari', 'Al-Latif'] },
      { step: 3, description: 'Root colonization — Al-Wadud\'s love written into the soil', namesActive: ['Al-Wadud', 'Al-Latif'] },
      { step: 4, description: 'Nutrient exchange — Ar-Razzaq flowing through fungal threads', namesActive: ['Ar-Razzaq', 'Al-Jami'] },
      { step: 5, description: 'Network formation — Al-Jami gathering what was scattered', namesActive: ['Al-Jami', 'Al-Wadud'] },
      { step: 6, description: 'Mutualism established — the underground community complete', namesActive: ['Al-Wadud', 'Ar-Razzaq', 'Al-Khaliq'] },
    ],
  },
  {
    seqId: 's5',
    seqTitle: 'Amphibian Metamorphosis',
    city: 'Detroit',
    biome: 'Wetland · Transition Zone',
    ayah: 'He creates you in the wombs of your mothers, creation after creation, within three darknesses. (39:6)',
    steps: [
      { step: 1, description: 'Egg deposited in water — Al-Khaliq creates in the appropriate medium', namesActive: ['Al-Khaliq', 'Al-Bari'] },
      { step: 2, description: 'Tadpole hatches — first body plan: aquatic, herbivorous', namesActive: ['Al-Muhyi', 'Al-Bari'] },
      { step: 3, description: 'Aquatic life — Al-Musawwir fashioning the first form', namesActive: ['Al-Musawwir', 'Ar-Razzaq'] },
      { step: 4, description: 'Metamorphic climax — Al-Muhawwil transforming the body', namesActive: ['Al-Muhawwil', 'Al-Bari', 'Al-Musawwir'] },
      { step: 5, description: 'Terrestrial emergence — new lungs from gill tissue', namesActive: ['Al-Bari', 'Al-Muhyi'] },
      { step: 6, description: 'Adult form complete — Al-Musawwir\'s second fashioning', namesActive: ['Al-Musawwir', 'Al-Khaliq', 'Al-Bari'] },
    ],
  },
  {
    seqId: 's6',
    seqTitle: 'Salmon Run',
    city: 'Portland',
    biome: 'Pacific Rainforest · Migratory Layer',
    ayah: 'And He it is who has subjected the sea that you may eat from it fresh and tender meat. (16:14)',
    steps: [
      { step: 1, description: 'Ocean feeding — Ar-Razzaq providing in the deep', namesActive: ['Ar-Razzaq', 'Al-Khaliq'] },
      { step: 2, description: 'Coastal approach — Al-Hadi guiding without books or teachers', namesActive: ['Al-Hadi', 'Al-Mateen'] },
      { step: 3, description: 'River entry — the salmon knows its birth stream by smell', namesActive: ['Al-Hadi', 'Al-Khabir'] },
      { step: 4, description: 'Upstream migration — Al-Mateen\'s strength in the current', namesActive: ['Al-Mateen', 'Al-Qadir'] },
      { step: 5, description: 'Spawning — Al-Khaliq creating the next generation', namesActive: ['Al-Khaliq', 'Al-Muhyi', 'Ar-Razzaq'] },
      { step: 6, description: 'Return to earth — the salmon body becomes provision for the forest', namesActive: ['Ar-Razzaq', 'Al-Muhyi', 'Al-Khaliq'] },
    ],
  },
];

// Keep backward compatibility with null export
const SUNNAT_TIMELINES: SunnatTimeline[] = SUNNAT_TIMELINES_DATA;

export { SUNNAT_TIMELINES };

// ─── FILTER CONSTANTS ─────────────────────────────────────────────────────────

const NAME_CATEGORIES: string[] = [
  'All', 'Creation', 'Provision', 'Knowledge', 'Power',
  'Justice', 'Mercy', 'Guidance', 'Transformation', 'Community', 'Hidden',
];

export { NAME_CATEGORIES };

const KEY_FILTERS: { id: string; label: string }[] = [
  { id: 'all', label: 'All Keys' },
  { id: 'AYAT', label: 'AYAT (01)' },
  { id: 'HAAL', label: 'HAAL (02)' },
  { id: 'TAQWAH', label: 'TAQWAH (03)' },
  { id: 'MIZAN', label: 'MIZAN (04)' },
  { id: 'AMANAH', label: 'AMANAH (05)' },
];

export { KEY_FILTERS };

const SEQUENCE_FILTERS: { id: string; label: string }[] = [
  { id: 'all', label: 'All Sequences' },
  ...Object.entries(SEQUENCE_REGISTRY).map(([id, seq]) => ({
    id,
    label: `S${String(seq.num).padStart(2, '0')} — ${seq.title}`,
  })),
];

export { SEQUENCE_FILTERS };

function SunnatTimeline(...args: any[]): any {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: SunnatTimeline is not implemented yet.', args);
  return null;
}

